/**
 * UTM Tracker Utility
 * Handles reading and storing UTM parameters from URL for partner tracking
 */

interface UTMParams {
  source?: string;
  medium?: string;
  campaign?: string;
  partnerId?: string;
}

const UTM_STORAGE_KEY = 'aidating_utm_params';
const PARTNER_ID_KEY = 'aidating_partner_id';

/**
 * Extract UTM parameters from current URL
 */
export const extractUTMParams = (): UTMParams => {
  const urlParams = new URLSearchParams(window.location.search);
  
  return {
    source: urlParams.get('utm_source') || undefined,
    medium: urlParams.get('utm_medium') || undefined,
    campaign: urlParams.get('utm_campaign') || undefined,
    partnerId: urlParams.get('utm_partner') || undefined
  };
};

/**
 * Store UTM parameters in sessionStorage
 */
export const storeUTMParams = (params: UTMParams): void => {
  if (Object.keys(params).length > 0) {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
    
    // Store partner ID separately for quick access
    if (params.partnerId) {
      sessionStorage.setItem(PARTNER_ID_KEY, params.partnerId);
    }
  }
};

/**
 * Get stored UTM parameters from sessionStorage
 */
export const getStoredUTMParams = (): UTMParams | null => {
  const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
  return stored ? JSON.parse(stored) : null;
};

/**
 * Get stored partner ID
 */
export const getPartnerID = (): string | null => {
  return sessionStorage.getItem(PARTNER_ID_KEY);
};

/**
 * Initialize UTM tracking on page load
 * Should be called once when the app initializes
 */
export const initializeUTMTracking = (): void => {
  const utmParams = extractUTMParams();
  
  // Only store if we have actual UTM parameters
  if (utmParams.source || utmParams.medium || utmParams.campaign || utmParams.partnerId) {
    storeUTMParams(utmParams);
    
    // Log for debugging (remove in production if needed)
    console.log('[UTM Tracker] Parameters stored:', utmParams);
  }
};

/**
 * Append partner ID to message if available
 */
export const appendPartnerIDToMessage = (message: string): string => {
  const partnerId = getPartnerID();
  
  if (partnerId) {
    return `${message} [partner ID:${partnerId}]`;
  }
  
  return message;
};

/**
 * Generate partner link with UTM parameters
 */
export const generatePartnerLink = (partnerId: string, baseUrl: string = window.location.origin): string => {
  const url = new URL(baseUrl);
  url.searchParams.set('utm_source', 'partner');
  url.searchParams.set('utm_medium', 'referral');
  url.searchParams.set('utm_campaign', 'partner_program');
  url.searchParams.set('utm_partner', partnerId);
  
  return url.toString();
};

/**
 * Get all 10 partner links (IDs 00421-00430)
 */
export const getAllPartnerLinks = (baseUrl: string = window.location.origin): Record<string, string> => {
  const links: Record<string, string> = {};
  
  for (let i = 1; i <= 10; i++) {
    const partnerId = `00${420 + i}`;
    links[`Partner ${i}`] = generatePartnerLink(partnerId, baseUrl);
  }
  
  return links;
};